import { useEffect, useState } from "react";
import { api, fmtINR, STATUS_COLORS, STATUS_LABEL } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import Pagination from "@/components/Pagination";
import WhatsAppNudge from "@/components/WhatsAppNudge";

const PAGE_SIZE = 10;

function List({ title, statuses, testid, subtitle }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusIdx, setStatusIdx] = useState(0);
  const nav = useNavigate();

  // When the status list contains multiple statuses (e.g. approvals =
  // ["submitted","resubmitted"]), we fetch each in a paginated call and stitch
  // them locally so that per-status pagination remains correct.
  // In practice both approval and payment queues use short arrays.
  useEffect(() => { setPage(1); }, [statuses.join(",")]);
  useEffect(() => {
    // For simplicity: page across the *concatenation* by querying each status
    // in turn and slicing client-side for the current page window.
    Promise.all(
      statuses.map((s) => api.get("/expenses", { params: { status: s, page: 1, page_size: 500 } })
        .then((r) => (r.data.items || [])))
    ).then((rs) => {
      const all = rs.flat();
      setTotal(all.length);
      const start = (page - 1) * PAGE_SIZE;
      setItems(all.slice(start, start + PAGE_SIZE));
    });
    // eslint-disable-next-line
  }, [statuses.join(","), page]);

  return (
    <div className="p-4 lg:p-8 space-y-6" data-testid={testid}>
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Queue</div>
        <h1 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight">{title}</h1>
        <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>
      </div>
      {/* Desktop table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4">Expense No</th>
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Centre</th>
              <th className="text-left p-4">Category</th>
              <th className="text-left p-4">Vendor</th>
              <th className="text-left p-4">Submitted By</th>
              <th className="text-right p-4">Amount</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4 w-14">Nudge</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {items.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-zinc-400">Nothing pending — all clear.</td></tr>}
            {items.map((e) => {
              const pending = ["submitted", "resubmitted", "in_review"].includes(e.status);
              return (
                <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => nav(`/expenses/${e.id}`)}
                    data-testid={`queue-row-${e.expense_no}`}>
                  <td className="p-4 font-mono text-xs">{e.expense_no}</td>
                  <td className="p-4 text-zinc-600">{e.expense_date}</td>
                  <td className="p-4">{e.centre_code}</td>
                  <td className="p-4">{e.category}</td>
                  <td className="p-4 text-zinc-600">{e.vendor || "—"}</td>
                  <td className="p-4 text-zinc-600">{e.created_by_name}</td>
                  <td className="p-4 text-right font-medium">{fmtINR(e.total_amount)}</td>
                  <td className="p-4"><span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${STATUS_COLORS[e.status]}`}>{STATUS_LABEL[e.status]}</span></td>
                  <td className="p-2 text-right">
                    {pending && <WhatsAppNudge kind="expense" id={e.id} number={e.expense_no} variant="icon" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination page={page} pageSize={PAGE_SIZE} total={total}
          onPageChange={setPage} testid={`${testid}-pagination`} />
      </div>
      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {items.length === 0 && (
          <div className="p-8 text-center text-zinc-400 bg-white border border-zinc-200">Nothing pending — all clear.</div>
        )}
        {items.map((e) => {
          const pending = ["submitted", "resubmitted", "in_review"].includes(e.status);
          return (
            <div key={e.id}
                 onClick={() => nav(`/expenses/${e.id}`)}
                 className="bg-white border border-zinc-200 p-4 active:bg-zinc-50 cursor-pointer"
                 data-testid={`queue-card-${e.expense_no}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-zinc-500">{e.expense_no}</div>
                  <div className="text-sm font-medium mt-1 truncate">{e.vendor || e.category || "—"}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">
                    {e.centre_code} · {e.expense_date} · {e.created_by_name}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading text-lg tabular-nums">{fmtINR(e.total_amount)}</div>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border ${STATUS_COLORS[e.status]}`}>
                    {STATUS_LABEL[e.status]}
                  </span>
                </div>
              </div>
              {pending && (
                <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-end">
                  <WhatsAppNudge kind="expense" id={e.id} number={e.expense_no} variant="inline" />
                </div>
              )}
            </div>
          );
        })}
        <div className="bg-white border border-zinc-200">
          <Pagination page={page} pageSize={PAGE_SIZE} total={total}
            onPageChange={setPage} testid={`${testid}-pagination-mobile`} />
        </div>
      </div>
    </div>
  );
}

export function Approvals() {
  return <List title="Approvals" subtitle="Expenses awaiting your review." statuses={["submitted", "resubmitted"]} testid="approvals-root" />;
}
export function Payments() {
  return <List title="Payments" subtitle="Approved expenses ready for disbursement." statuses={["approved"]} testid="payments-root" />;
}
